import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Users, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';

const FOREST = '#2C4A3B';
const TERRA = '#C47A5E';
const CREAM = '#F9F6F0';
const CHARCOAL = '#1C1917';

export default function AcceptInvitePage({ token: authToken }) {
  const { token } = useParams();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/practice/invitation/${token}`);
        setPreview(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or expired invitation');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const goRegister = () => {
    const q = preview?.email ? `?invite=${token}&email=${encodeURIComponent(preview.email)}` : `?invite=${token}`;
    navigate(`/register${q}`);
  };

  const goLogin = () => {
    navigate(`/login?invite=${token}`);
  };

  const acceptNow = async () => {
    if (!authToken) { goLogin(); return; }
    setAccepting(true);
    try {
      const res = await axios.post(
        `${API}/practice/invitation/${token}/accept`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      toast.success(`Joined ${res.data.practice_name}`);
      navigate('/practice');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ background: CREAM, fontFamily: "'Manrope', sans-serif" }}
      data-testid="accept-invite-page"
    >
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-xl p-8 sm:p-10 border" style={{ borderColor: '#E3E8E4' }}>
          {loading && (
            <div className="flex flex-col items-center text-stone-500 py-10">
              <Loader2 className="w-6 h-6 animate-spin mb-3" />
              <p className="text-sm">Loading your invitation…</p>
            </div>
          )}

          {!loading && error && (
            <div className="text-center py-6" data-testid="invite-error">
              <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4" style={{ background: '#FEE9E2' }}>
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <h1 className="text-xl font-bold mb-2" style={{ color: CHARCOAL }}>Invitation not available</h1>
              <p className="text-sm text-stone-500 mb-6">{error}</p>
              <Button onClick={() => navigate('/landing')} className="h-11 rounded-xl text-white font-semibold px-6" style={{ background: FOREST }}>
                Back to home
              </Button>
            </div>
          )}

          {!loading && preview && preview.status !== 'pending' && (
            <div className="text-center py-6" data-testid="invite-status-closed">
              <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4" style={{ background: '#E3E8E4' }}>
                <CheckCircle2 className="w-6 h-6" style={{ color: FOREST }} />
              </div>
              <h1 className="text-xl font-bold mb-2" style={{ color: CHARCOAL }}>This invitation is no longer active</h1>
              <p className="text-sm text-stone-500 mb-6">
                Invitation status: <strong className="capitalize">{preview.status}</strong>. Ask the practice owner to send a new one.
              </p>
              <Button onClick={() => navigate('/landing')} className="h-11 rounded-xl text-white font-semibold px-6" style={{ background: FOREST }}>
                Back to home
              </Button>
            </div>
          )}

          {!loading && preview && preview.status === 'pending' && preview.expired && (
            <div className="text-center py-6" data-testid="invite-expired">
              <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4" style={{ background: '#FEE9E2' }}>
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <h1 className="text-xl font-bold mb-2" style={{ color: CHARCOAL }}>This invitation has expired</h1>
              <p className="text-sm text-stone-500 mb-6">Invitations are valid for 14 days. Ask the practice owner to send a fresh link.</p>
              <Button onClick={() => navigate('/landing')} className="h-11 rounded-xl text-white font-semibold px-6" style={{ background: FOREST }}>
                Back to home
              </Button>
            </div>
          )}

          {!loading && preview && preview.status === 'pending' && !preview.expired && (
            <div className="text-center">
              <div className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-5" style={{ background: FOREST }}>
                <Users className="w-6 h-6 text-white" />
              </div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] mb-2" style={{ color: TERRA }}>You're invited</p>
              <h1 className="text-2xl font-bold mb-2" style={{ color: CHARCOAL }}>
                Join {preview.practice_name}
              </h1>
              <p className="text-sm text-stone-500 mb-1">
                Invitation for <strong>{preview.email}</strong>
              </p>
              <p className="text-xs text-stone-400 mb-6">
                on Plexura AI Scribe · HIPAA-compliant mental health scribing
              </p>

              <div className="space-y-3">
                {authToken ? (
                  <Button
                    onClick={acceptNow}
                    disabled={accepting}
                    className="w-full h-11 rounded-xl text-white font-semibold"
                    style={{ background: FOREST }}
                    data-testid="accept-invite-btn"
                  >
                    {accepting ? 'Joining…' : `Accept & join ${preview.practice_name}`}
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={goRegister}
                      className="w-full h-11 rounded-xl text-white font-semibold"
                      style={{ background: FOREST }}
                      data-testid="invite-register-btn"
                    >
                      Create account & accept
                    </Button>
                    <Button
                      onClick={goLogin}
                      variant="outline"
                      className="w-full h-11 rounded-xl"
                      data-testid="invite-login-btn"
                    >
                      I already have an account
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
